import React, { useEffect, useMemo, useRef } from 'react';
import { CSSTransition } from 'react-transition-group';
import { compose } from 'redux';

import PageLayout from 'layouts/PageLayout';
import Sidebar from 'scenes/Sidebar';
import BuyCreditsDialog from 'scenes/Dialog/components/BuyCreditsDialog';
import JobCredits from 'scenes/Sidebar/components/JobCredits';
import RecruiterProcess from 'scenes/RecruiterProcess';
import List from 'scenes/List/';
import ProcessItem from 'scenes/List/components/ProcessItem';
import ProcessSearch from 'scenes/RecruiterProcess/components/ProcessSearch';
import PanelContainer from 'components/PanelContainer';
import PagePagination from 'components/PagePagination';
import BasicButton from 'components/BasicButton';
import InBoundsHistory from 'scenes/Sidebar/components/InBoundsHistory';
import GridHistory from 'components/GridHistory';
import ListSearchMinimalContentLoader from 'components/ListSearchMinimalContentLoader';
import FiltersContentLoader from 'components/FiltersContentLoader';
import HorizontalLoader from 'components/HorizontalLoader';
import ApplicantHistoryWidget from 'scenes/Sidebar/components/ApplicantHistoryWidget';
import FormDialog from 'scenes/Dialog/components/FormDialog';
import BasicDialog from 'scenes/Dialog/components/BasicDialog';
import ResponseErrorDialog from 'scenes/Dialog/components/ResponseErrorDialog';
import SetupEmailReassignForm from 'scenes/Form/components/SetupEmailReassignForm';

import withViewStatisticsAction from 'hocs/withViewStatisticsAction';
import withProcessActions from 'hocs/withProcessActions';
import { useGrid } from 'hooks/useGrid';
import { updateHistory } from 'helpers/grid-history';
import { Role } from 'helpers/user';
import { POPUP_STYLES } from 'constants/popup-styles';
import { ANIMATION_TIMEOUT_APPEAR } from 'constants/animation.js';
import { ENDPOINT } from 'constants/endpoints';
import { ROUTE } from 'constants/route';

import './styles.css';

const ManageProcess = props => {
  const {
    history,
    match: {
      params: { closed = null },
    },
    location,
    location: { pathname },
    user,
    processAction: {
      jobPush,
      jobRefresh,
      jobClose,
      jobReopen,
      jobAssign,
      jobRemoveAssign,
      jobRemove,
      jobViewApplicants,
    },
    viewStatisticsJobExtended,
  } = props;

  const refAppHistoryWidget = useRef();

  const grid = useGrid(ENDPOINT.COLLECTION.MANAGE_JOBS, params => {
    updateHistory(history, params);
  });

  const [showSearchBar, isRestrictedFirm, isRecruiterDefault] = useMemo(() => {
    return [
      Role.isRecruiterPremiumRecFirm(user),
      Role.isRecruiterRestricted(user),
      Role.isRecruiterDefault(user),
    ];
  }, [user]);

  useEffect(() => {
    if (location.search) {
      const params = new URLSearchParams(location.search);
      const action = params.get('action');

      if ('reassign' === action) {
        FormDialog({
          title: 'Please select a Recruiter to re-assign these Searches',
          modeless: false,
          compact: true,
          popupStyles: { ...POPUP_STYLES.formPopupStyles, maxWidth: '540px' },
          popupInnerStyles: POPUP_STYLES.formInnerStyles,
          popupTitleStyles: POPUP_STYLES.formTitleStyles,
          form: (actions, params) => (
            <SetupEmailReassignForm actions={actions} params={params} />
          ),
        })
          .then(
            () =>
              BasicDialog({
                bodyRender: 'Email Setup has been re-assigned successfully',
                popupStyles: {
                  maxWidth: '642px',
                  padding: '30px 25px 30px 25px',
                },
              }),
            response => {
              response && ResponseErrorDialog({ response });
            }
          )
          .finally(() => history.replace(history.location.pathname));
      }
    }
  }, [pathname]);

  const handleTabChange = tab =>
    history.push(
      pathname.replace('/closed', '') + (tab === 'active' ? '' : '/closed')
    );

  const handlePush = ({ ProcessID } = process) => {
    jobPush(ProcessID);
  };

  const handleRefresh = ({ ProcessID } = process) => {
    jobRefresh(ProcessID);
  };

  const handleClose = ({ ProcessID } = process) => {
    jobClose(ProcessID).then(success => {
      success && grid.actions.fetch();
    });
  };

  const handleReopen = ({ ProcessID } = process) => {
    jobReopen(ProcessID).then(success => success && grid.actions.fetch());
  };

  const handleAssign = ({ ProcessID } = process) => {
    jobAssign(ProcessID).then(success => success && grid.actions.fetch());
  };

  const handleRemoveAssign = ({ ProcessID } = process) => {
    jobRemoveAssign(ProcessID).then(success => success && grid.actions.fetch());
  };

  const handleViewStatistics = ({ ProcessID } = process) => {
    viewStatisticsJobExtended(ProcessID);
  };

  const handleViewApplicants = ({ ProcessID } = process) => {
    jobViewApplicants(ProcessID);
  };

  const handleDelete = ({ ProcessID } = process) => {
    jobRemove(ProcessID).then(success => {
      success && grid.actions.fetch();
      refAppHistoryWidget.current && refAppHistoryWidget.current.refresh();
    });
  };

  const handleClickCreateSearch = () => {
    if (isRestrictedFirm && !grid.data.credits) {
      BuyCreditsDialog({ history });
    } else {
      history.push(ROUTE.SEARCH_PROCESS_ADD);
    }
  };

  const title = `${closed ? `Closed` : `Active`} Searches`;

  return (
    <GridHistory
      grid={grid}
      history={history}
      matchParams={{
        closed: closed ? 1 : 0,
      }}
    >
      <PageLayout
        pageClass="manage-process"
        pageTitle="Manage Process"
        mainContent={
          <>
            {grid.data.init ? (
              <PanelContainer styles={{ marginBottom: '15px' }}>
                <div className="manage-process__header">
                  <h2 className="manage-process__title">
                    Run a Confidential Search
                  </h2>
                  <div className="manage-process__action">
                    <BasicButton
                      btnWidth={200}
                      btnHeight={45}
                      btnFontWeight={600}
                      btnText="Create Job Posting"
                      btnArrow={true}
                      btnBg="#fff"
                      btnHoverBg="#f40"
                      controlFunc={handleClickCreateSearch}
                    />
                  </div>
                </div>
                <div className="manage-process__search">
                  <ProcessSearch
                    search={grid.data.params.search || false}
                    showSearchBar={showSearchBar}
                    searchPlaceholder="Search Process"
                    onSendSearchQueryHandler={grid.actions.search}
                    onTabSelectHandler={handleTabChange}
                    selectedTab={closed ? 'closed' : 'active'}
                  />
                </div>
              </PanelContainer>
            ) : (
              <ListSearchMinimalContentLoader />
            )}

            {grid.data.init && (
              <CSSTransition
                in={grid.data.init}
                appear={true}
                timeout={ANIMATION_TIMEOUT_APPEAR}
                classNames="fade"
              >
                {grid.data.isFetching ? (
                  <div className="horizontal-loader__container">
                    <HorizontalLoader />
                  </div>
                ) : (
                  <RecruiterProcess>
                    <div className="page__description">
                      <h3 className="page__subtitle">{title}</h3>
                    </div>

                    <List
                      items={grid.data.rows}
                      itemRender={(item, actions) => (
                        <ProcessItem
                          key={item.ProcessID}
                          data={item}
                          actions={{
                            handlePush,
                            handleRefresh,
                            handleClose,
                            handleReopen,
                            handleAssign,
                            handleRemoveAssign,
                            handleDelete,
                            handleViewStatistics,
                            handleViewApplicants,
                          }}
                          isRestricted={isRestrictedFirm}
                          allowAssign={
                            grid.data.permissions.AssignProcessToFirm
                          }
                        />
                      )}
                    />

                    {grid.data.rowsCount > grid.data.perPage && (
                      <div>
                        <PagePagination
                          page={grid.data.page}
                          perPage={grid.data.perPage}
                          count={grid.data.rowsCount}
                          pageRangeDisplayed={5}
                          controlFunc={grid.actions.readPage}
                          hideFirstLastPages={false}
                          hideNavigation={true}
                        />
                      </div>
                    )}
                  </RecruiterProcess>
                )}
              </CSSTransition>
            )}
          </>
        }
        sidebarContent={
          grid.data.init ? (
            <Sidebar>
              {isRestrictedFirm && (
                <JobCredits creditsCount={grid.data.credits || 0} />
              )}
              {!isRecruiterDefault && (
                <ApplicantHistoryWidget ref={refAppHistoryWidget} />
              )}
              <InBoundsHistory isRecruiter />
            </Sidebar>
          ) : (
            <FiltersContentLoader />
          )
        }
      />
    </GridHistory>
  );
};

export default compose(
  withViewStatisticsAction,
  withProcessActions
)(ManageProcess);
